<script setup>
// Recordatorio cada 1 hora para marcar conversaciones como Resuelto al
// finalizar la conversación (Victor 2026-05-28). Lo ven todos los usuarios de
// la cuenta MENOS los super_admin. Aparece centrado, bloquea el fondo, y se
// cierra con "Leído, entendido" o Enter. Al cerrar se reagenda 1 h.
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';

const INTERVAL_MS = 60 * 60 * 1000; // 1 hora

const store = useStore();

const visible = ref(false);
let timerId = null;

const currentUser = computed(() => store.getters.getCurrentUser || {});
const currentAccountId = computed(() => store.getters.getCurrentAccountId);

const isSuperAdmin = computed(() => {
  const accId = Number(currentAccountId.value);
  const account = currentUser.value?.accounts?.find(
    a => Number(a.id) === accId
  );
  return account?.super_admin === true;
});

function scheduleNext() {
  if (timerId) clearTimeout(timerId);
  timerId = setTimeout(() => {
    visible.value = true;
  }, INTERVAL_MS);
}

function close() {
  visible.value = false;
  scheduleNext();
}

function onKey(e) {
  if (!visible.value) return;
  if (e.key === 'Enter' || e.key === 'Escape') {
    e.preventDefault();
    close();
  }
}

onMounted(() => {
  if (isSuperAdmin.value) return;
  scheduleNext();
  window.addEventListener('keydown', onKey);
});

onBeforeUnmount(() => {
  if (timerId) clearTimeout(timerId);
  window.removeEventListener('keydown', onKey);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible && !isSuperAdmin"
      class="rrm-stage"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="rrm-title"
    >
      <div class="rrm-backdrop"></div>
      <div class="rrm-modal">
        <div class="rrm-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            <path d="M9 11l2 2 4-4" stroke-width="1.8"/>
          </svg>
        </div>
        <div class="rrm-eyebrow">
          Recordatorio
          <span class="rrm-sep">·</span>
          Conversaciones
        </div>
        <h2 id="rrm-title" class="rrm-title">
          Marca como <span class="rrm-gold">Resuelto</span> al finalizar el chat
        </h2>
        <div class="rrm-body">
          Recuerda que <b>al finalizar la conversación con el cliente</b>, esta se debe marcar como <span class="rrm-gold rrm-strong">Resuelto</span>.<br><br>
          Evita atrasos y el cierre del sistema por acumulación de conversaciones <b>finalizadas pero no cerradas</b>.
        </div>
        <div class="rrm-divider"></div>
        <div class="rrm-btn-row">
          <button class="rrm-btn" type="button" @click="close">
            Leído, entendido
          </button>
        </div>
        <div class="rrm-shortcut">
          o presiona <kbd>Enter</kbd> para cerrar
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.rrm-stage {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.rrm-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.rrm-modal {
  position: relative;
  width: 520px;
  max-width: 100%;
  background: linear-gradient(180deg, #101015 0%, #0c0c11 100%);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 22px;
  padding: 40px 40px 32px;
  color: #e7e7ea;
  box-shadow:
    0 30px 80px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(201, 163, 90, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  animation: rrm-rise 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
}
.rrm-modal::before {
  content: '';
  position: absolute;
  left: 36px;
  right: 36px;
  top: -1px;
  height: 1px;
  background: linear-gradient(90deg, transparent, #c9a35a 50%, transparent);
  opacity: 0.55;
}
.rrm-modal::after {
  content: '';
  position: absolute;
  left: 36px;
  right: 36px;
  bottom: -1px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(201, 163, 90, 0.30) 50%, transparent);
}
@keyframes rrm-rise {
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.rrm-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, rgba(201, 163, 90, 0.20), rgba(201, 163, 90, 0.04) 60%, transparent 75%);
  border: 1px solid rgba(201, 163, 90, 0.20);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 22px;
  position: relative;
  color: #c9a35a;
}
.rrm-icon::after {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 1px solid rgba(201, 163, 90, 0.08);
  animation: rrm-pulse 2.8s ease-out infinite;
}
.rrm-icon svg {
  width: 30px;
  height: 30px;
}
@keyframes rrm-pulse {
  0% { transform: scale(1); opacity: 0.5; }
  70% { transform: scale(1.25); opacity: 0; }
  100% { transform: scale(1.25); opacity: 0; }
}
.rrm-eyebrow {
  text-align: center;
  font-size: 10px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #c9a35a;
  font-weight: 600;
  margin-bottom: 10px;
}
.rrm-sep {
  margin: 0 8px;
  color: #5a5a62;
  font-weight: 400;
}
.rrm-title {
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 18px;
  line-height: 1.3;
}
.rrm-body {
  color: #8a8a92;
  font-size: 15px;
  line-height: 1.65;
  text-align: center;
  margin-bottom: 26px;
}
.rrm-body b {
  color: #e7e7ea;
  font-weight: 600;
}
.rrm-gold {
  color: #c9a35a;
}
.rrm-strong {
  font-weight: 600;
}
.rrm-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.10) 30%, rgba(255, 255, 255, 0.10) 70%, transparent);
  margin: 24px 0 22px;
}
.rrm-btn-row {
  display: flex;
  justify-content: center;
}
.rrm-btn {
  appearance: none;
  border: none;
  cursor: pointer;
  padding: 14px 36px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #0a0a0c;
  background: linear-gradient(180deg, #d4ac63 0%, #b88e48 100%);
  border-radius: 10px;
  box-shadow:
    0 6px 18px rgba(201, 163, 90, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset,
    0 -1px 0 rgba(0, 0, 0, 0.15) inset;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
}
.rrm-btn:hover {
  transform: translateY(-1px);
  box-shadow:
    0 10px 24px rgba(201, 163, 90, 0.32),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset,
    0 -1px 0 rgba(0, 0, 0, 0.15) inset;
}
.rrm-btn:active {
  transform: translateY(0);
}
.rrm-shortcut {
  text-align: center;
  font-size: 11px;
  color: #5a5a62;
  margin-top: 14px;
}
.rrm-shortcut kbd {
  display: inline-block;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  padding: 2px 7px;
  background: #1a1a20;
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 5px;
  color: #8a8a92;
  margin: 0 2px;
}
</style>
